import axios from "axios";
import type {
  CurrentLegalDocument,
  DocKey,
  LegalDocument,
  PublishLegalDocumentInput,
} from "@/interfaces/legal";
import { apiClient } from "@/lib/apiClient";

/** Rethrows the server's error message (not a bare axios error) so the UI can show it. */
function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    throw new Error(error.response.data.error as string);
  }
  throw new Error(fallback);
}

/**
 * The version the signup screen must display and send back.
 *
 * Public on the server, so this works before the user has a session. Returns null when nothing has
 * been published: signup then has no version to name and says so, rather than asking someone to
 * accept a document that does not exist.
 */
export async function fetchCurrentTerms(
  docKey: DocKey = "platform_terms",
): Promise<CurrentLegalDocument | null> {
  try {
    const { data } = await apiClient.get<{ success: boolean; data: CurrentLegalDocument }>(
      `/legal/current?doc=${docKey}`,
    );
    return data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    rethrowApiError(error, "Couldn't load the current Terms & Conditions.");
  }
}

/** Every published version, newest upload first. GFG super-admin only, gated server-side. */
export async function fetchLegalDocuments(
  docKey: DocKey = "platform_terms",
): Promise<LegalDocument[]> {
  const { data } = await apiClient.get<{ success: boolean; data: LegalDocument[] }>(
    `/admin/system/legal-documents?doc=${docKey}`,
  );
  return data.data;
}

/**
 * Publish a version: the file goes THROUGH the API as multipart, not to R2 by presigned PUT, so the
 * server hashes the exact bytes it stores.
 *
 * No Content-Type is set here on purpose. It has to be the browser's, because only the browser
 * knows the boundary string that makes the body parseable — apiClient's request interceptor drops
 * its JSON default whenever the body is FormData, which is what lets that happen.
 */
export async function publishLegalDocument(
  input: PublishLegalDocumentInput,
): Promise<LegalDocument> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("docKey", input.docKey);
  form.append("effectiveFrom", input.effectiveFrom);
  form.append("isMaterial", String(input.isMaterial));

  try {
    const { data } = await apiClient.post<{ success: boolean; data: LegalDocument }>(
      "/admin/system/legal-documents",
      form,
    );
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't publish the document. Please try again.");
  }
}
